// @ts-nocheck
/* Starter Sync - SecondaryNavigation */

import { getCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Popover, { PopoverPresentationProps, PositionStyle } from "@insite/mobius/Popover/Popover";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronDown, { ChevronDownOffset } from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import SettingsLine, { SettingsLineOffset } from "@store/Library/Components/Toolkit/Icons/Settings/SettingsLine";
import NavigationModeMenu, {
    NavigationModeCookieName,
    NavigationModeMenuStyles,
    useShowNavigationModeMenu,
} from "@store/Overrides/Components/NavigationModeMenu";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { linkStyles } from "@store/Themes/common/mixins/link";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, Ref, useCallback, useMemo, useRef, useState } from "react";
import { css } from "styled-components";

const enum fields {
    showIcon = "showIcon",
    showInventoryToggle = "showInventoryToggle",
    showPricingToggle = "showPricingToggle",
    popoverAlignement = "popoverAlignement",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
        [fields.showInventoryToggle]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.popoverAlignement]: "right" | "left";
    };
}

type Props = OwnProps;

export interface SettingsMenuStyles {
    link?: LinkPresentationProps;
    iconDropdown?: IconPresentationProps;
    popover?: PopoverPresentationProps;
    content?: InjectableCss;
    section?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    inventoryToggle?: ShowInventoryToggleStyles;
    pricingToggle?: ShowPricingToggleStyles;
}

export const settingsMenuStyles: SettingsMenuStyles = {
    link: {
        icon: {
            position: "left",
            iconProps: {
                src: SettingsLine,
                color: "inherit",
                css: css`
                    ${iconScalableStyles(pxToRem(20), SettingsLineOffset)}
                    margin-right: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
                `,
            },
        },
        typographyProps: {
            css: css`
                position: relative;
                padding-right: 1.2em;
            `,
        },
        css: css`
            ${linkStyles("inherit", "menu.secondaryNavigation.hover", false, true)}

            &[aria-expanded="true"] {
                text-decoration: underline;
            }
        `,
    },
    iconDropdown: {
        src: ChevronDown,
        color: "inherit",
        css: css`
            ${iconScalableStyles("1em", ChevronDownOffset)}
            position: absolute;
            top: 50%;
            right: 0;
            transform: translateY(-50%);
        `,
    },
    popover: {
        contentBodyProps: {
            as: "div",
            css: css`
                ${boxStyles(null)}
                max-height: none;
                min-width: 100%;
                color: ${getColor("text")};
                white-space: nowrap;

                ${({ theme }) => css`
                    font-size: ${theme.design.typography.size.base};
                    font-weight: ${theme.design.typography.weight.base};
                    line-height: ${theme.design.typography.lineHeight.base};
                `}
            `,
        },
    },
    section: {
        css: css`
            display: flex;
            padding: ${pxToRem(12, 16)};
            border-top: 1px solid ${getColor("common.border")};

            &:first-child {
                border-top: 0;
            }
        `,
    },
};

export const SettingsMenu: FC<Props> = ({
    fields: {
        showIcon,
        showInventoryToggle: showInventoryToggleField,
        showPricingToggle: showPricingToggleField,
        popoverAlignement,
    },
}) => {
    const displayPricingAndInventory = useAppSelector(
        state => state.context.session?.displayPricingAndInventory || false,
    );

    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const showToggles = displayPricingAndInventory && currentMode === "Storefront";

    const popoverRef = useRef<HTMLUListElement>(null);
    const popoverBodyRef = useRef<HTMLUListElement>(null);

    const [navigationModeModalScrimRef, baseSetNavigationModeModalScrimRef] = useState<any>(null);
    const setNavigationModeModalScrimRef = (ref: Ref<HTMLDivElement>) => {
        setTimeout(() => {
            baseSetNavigationModeModalScrimRef(ref);
        });
    };

    const styles = useMemo(() => {
        return mergeProps<SettingsMenuStyles>(settingsMenuStyles, {
            link: {
                icon: showIcon ? undefined : (null as any),
            },
        });
    }, [showIcon]);

    const positionFunction = useCallback(() => {
        const result: PositionStyle = { position: "absolute" };

        result[popoverAlignement] = 0;

        return result;
    }, [popoverAlignement]);

    const showNavigationMode = useShowNavigationModeMenu();
    const showInventoryToggle = showInventoryToggleField && showToggles;
    const showPricingToggle = showPricingToggleField && showToggles;

    if (!showNavigationMode && !showInventoryToggle && !showPricingToggle) {
        return null;
    }

    return (
        <Popover
            {...styles.popover}
            controlsId="settingsMenu"
            contentBodyProps={{ ref: popoverBodyRef, ...styles.popover?.contentBodyProps }}
            positionFunction={positionFunction}
            ref={popoverRef}
            insideRefs={[popoverBodyRef, navigationModeModalScrimRef]}
            popoverTrigger={
                <Link {...styles.link}>
                    {translate("Navigation Settings")}
                    <Icon {...styles.iconDropdown} />
                </Link>
            }
        >
            <StyledWrapper {...styles.content}>
                {showNavigationMode && (
                    <StyledWrapper {...styles.section}>
                        <NavigationModeMenu
                            extendedStyles={styles.navigationModeMenu}
                            setModalOverlayScrimRef={setNavigationModeModalScrimRef}
                        />
                    </StyledWrapper>
                )}
                {showInventoryToggle && (
                    <StyledWrapper {...styles.section}>
                        <ShowInventoryToggle extendedStyles={styles.inventoryToggle} />
                    </StyledWrapper>
                )}
                {showPricingToggle && (
                    <StyledWrapper {...styles.section}>
                        <ShowPricingToggle extendedStyles={styles.pricingToggle} />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </Popover>
    );
};

const widgetModule: WidgetModule = {
    component: SettingsMenu,
    definition: {
        displayName: "Settings Menu",
        icon: "gear",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.popoverAlignement,
                displayName: "Menu Alignment",
                fieldType: "Contextual",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                isRequired: true,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show Pricing Toggle",
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show Inventory Toggle",
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
